import React, { useMemo, useState, useCallback } from 'react';
import { useQuery } from 'react-query';
import { Tooltip, Popover } from '@mui/material';
import { UnfoldMore, UnfoldLess, Edit, Close } from '@mui/icons-material';
import { useBaseHedgeMaterials, useDerivedMaterials } from '../../../hooks/useBaseHedgeMaterials';
import { useFormulasPrices } from '../../../hooks/useFormulasPrices';
import {
  useCommodexBudgetRatios, useSetCommodexBudgetRatio,
  useCommodexBudgetPrices, useSetCommodexBudgetPrice,
} from '../../../hooks/useCommodexBudgetRatios';
import { getDerivedHedges, getHedgePositions } from '../../../services/hedgesService';
import { getDemandPositions } from '../../../services/demandService';
import { getSupplyPositions } from '../../../services/supplyService';
import { useBaseCurrency } from '../../../hooks/useBaseCurrency';
import { useWatchRules, extractPolicyConfig } from '../../../hooks/useWatchRules';
import {
  calculateCommodexTable,
  CommodexData,
  CommodexCell,
  CommodexDerivedMaterial,
  CommodexBufferConfig,
  CommodexPosition,
  CommodexRatioPosition,
  CommodexHedge,
} from './commodexTableCalculator';

interface Props {
  accountId: number;
  baseMaterialId: string;
}

// One year forward — like the Terminal Coverage table, constrained to 12 months.
const HORIZON_MONTHS = 12;
// Selectable view horizons (calendar months) for the header control.
const HORIZON_OPTIONS = [6, 9, 12, 18, 24, 36];
const BUFFER_DEFAULT = 60;

// ─── Formatting ───────────────────────────────────────────────────────────────

const fmt = (n: number, decimals = 0) =>
  n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
const fmtRatio = (n: number) => (n > 0 ? n.toFixed(2) : '0.00');
const fmtPct = (n: number | null) => (n === null ? '—' : `${Math.round(n * 100)}%`);

// Muted grey for zero / empty values to keep the grid calm.
const ZERO_COLOR = '#cbd5e1';
// Distinct treatment for avg-ratio values that fell back to the deriving setting.
const FALLBACK_RATIO_COLOR = '#b45309';

const monthToPeriod = (monthKey: string): string => {
  const [y, m] = monthKey.split('-').map(Number);
  return `${y}-${String(m).padStart(2, '0')}-01`;
};

// Demand-shift options (months, forward/backward) for the header "what-if" control.
const DEMAND_SHIFT_OPTIONS = [-6, -3, -2, -1, 0, 1, 2, 3, 6];

/** Parse an agreed ratio from a supply position's notes (e.g. "ratio=1.80"). */
const parseRatio = (notes?: string | null): number => {
  if (!notes) return 0;
  const m = notes.match(/ratio=([\d.]+)/);
  return m ? parseFloat(m[1]) : 0;
};

// ─── Row definitions ──────────────────────────────────────────────────────────

type RowKey =
  | 'demand' | 'ratioOrders' | 'fixedOrders' | 'availableInventory'
  | 'openingInventory' | 'emergencyBuffer' | 'closingInventory'
  | 'avgRatio' | 'budgetRatio' | 'toOrder' | 'toPrice' | 'opCoverage';

interface RowDef {
  key: RowKey;
  label: string;
  help: string;          // shown as tooltip in help mode
  detail?: boolean;      // shown only when inventory expander is open
  editable?: boolean;    // budget ratio
  kind: 'qty' | 'ratio' | 'pct';
}

const ROWS: RowDef[] = [
  { key: 'demand',             label: 'ביקוש צפוי (MT)',        kind: 'qty',
    help: 'הביקוש החזוי לחומר בכל חודש, מתוך תחזיות הביקוש שנקלטו במערכת.' },
  { key: 'ratioOrders',        label: 'הזמנות רשיו (MT)',       kind: 'qty',
    help: 'הזמנות רכש שנסגרו על בסיס רשיו — הכמות מחויבת פיזית אך המחיר טרם קובע.' },
  { key: 'fixedOrders',        label: 'הזמנות מפוקססות (MT)',   kind: 'qty',
    help: 'הזמנות רכש עם מחיר קבוע (fixed). נספרת רק הכמות.' },
  { key: 'availableInventory', label: 'מלאי לניצול (MT)',       kind: 'qty',
    help: 'מלאי פתיחה בניכוי מלאי חירום — הכמות הפנויה בפועל לכיסוי ביקוש.' },
  { key: 'openingInventory',   label: 'מלאי פתיחה',              kind: 'qty', detail: true,
    help: 'המלאי בתחילת החודש — מלאי on-hand שנצבר, מתגלגל קדימה חודש-חודש.' },
  { key: 'emergencyBuffer',    label: 'מלאי חירום נדרש',         kind: 'qty', detail: true,
    help: 'מלאי הביטחון הנדרש לפי הגדרות הבאפר של החומר (משותף עם שאר המדיניות).' },
  { key: 'closingInventory',   label: 'מלאי סיום',               kind: 'qty', detail: true,
    help: 'המלאי בסוף החודש = מלאי פתיחה + הזמנות רשיו + מפוקססות − ביקוש (לא שלילי). מתגלגל למלאי הפתיחה של החודש הבא.' },
  { key: 'avgRatio',           label: 'רשיו ממוצע',              kind: 'ratio',
    help: 'רשיו משוקלל-כמות על פני כל ההזמנות. בהיעדר הזמנות — ערך ברירת מחדל מהגדרות הגזירה.' },
  { key: 'budgetRatio',        label: 'רשיו תקציב',              kind: 'ratio', editable: true,
    help: 'יעד רשיו מוגדר-משתמש. ניתן לעריכה ישירות בטבלה.' },
  { key: 'toOrder',            label: 'חוסרים — כמות להזמין',    kind: 'qty',
    help: 'הכמות שנותר להזמין = ביקוש − הזמנות רשיו − הזמנות מפוקססות − מלאי לניצול (לא שלילי).' },
  { key: 'toPrice',            label: 'חוסרים — כמות לתמחר',     kind: 'qty',
    help: 'הכמות שנותר לתמחר = כמות להזמין + הזמנות רשיו (שעדיין ללא מחיר קבוע).' },
  { key: 'opCoverage',         label: 'כיסוי תפעולי',            kind: 'pct',
    help: 'שיעור הכיסוי התפעולי = (הזמנות רשיו + מפוקססות + מלאי לניצול) / ביקוש.' },
];

function cellValue(key: RowKey, cell: CommodexCell): number | null {
  switch (key) {
    case 'demand':             return cell.demand;
    case 'ratioOrders':        return cell.ratioOrders;
    case 'fixedOrders':        return cell.fixedOrders;
    case 'availableInventory': return cell.availableInventory;
    case 'openingInventory':   return cell.openingInventory;
    case 'emergencyBuffer':    return cell.emergencyBuffer;
    case 'closingInventory':   return cell.closingInventory;
    case 'avgRatio':           return cell.avgRatio;
    case 'toOrder':            return cell.toOrder;
    case 'toPrice':            return cell.toPrice;
    case 'opCoverage':         return cell.opCoverage;
    default:                   return null;
  }
}

function formatValue(row: RowDef, v: number | null): string {
  if (row.kind === 'pct') return fmtPct(v);
  if (row.kind === 'ratio') return fmtRatio(v || 0);
  return fmt(v || 0);
}

// Color for operational coverage / to-order emphasis.
function opCoverageColor(v: number | null): string {
  if (v === null) return '#9ca3af';
  if (v >= 1) return '#059669';
  if (v >= 0.8) return '#d97706';
  return '#dc2626';
}

// ─── Formula inspector (Excel-style) ────────────────────────────────────────────

// Role of an operand in a formula — drives both cell tint and text color.
type OperandRole = 'plus' | 'minus' | 'divisor' | 'factor';

// Each computed row's inputs: which row, which month (offset: 0=same, -1=previous), and role.
interface DepCell { rowKey: RowKey; monthOffset: number; role: OperandRole }

const FORMULA_DEPS: Partial<Record<RowKey, DepCell[]>> = {
  // Opening inventory of a month is simply the previous month's ending inventory.
  openingInventory: [
    { rowKey: 'closingInventory', monthOffset: -1, role: 'plus' },
  ],
  // Ending inventory rolls the current month forward.
  closingInventory: [
    { rowKey: 'openingInventory', monthOffset: 0, role: 'plus' },
    { rowKey: 'ratioOrders', monthOffset: 0, role: 'plus' },
    { rowKey: 'fixedOrders', monthOffset: 0, role: 'plus' },
    { rowKey: 'demand', monthOffset: 0, role: 'minus' },
  ],
  availableInventory: [
    { rowKey: 'openingInventory', monthOffset: 0, role: 'plus' },
    { rowKey: 'emergencyBuffer', monthOffset: 0, role: 'minus' },
  ],
  toOrder: [
    { rowKey: 'demand', monthOffset: 0, role: 'plus' },
    { rowKey: 'ratioOrders', monthOffset: 0, role: 'minus' },
    { rowKey: 'fixedOrders', monthOffset: 0, role: 'minus' },
    { rowKey: 'availableInventory', monthOffset: 0, role: 'minus' },
  ],
  toPrice: [
    { rowKey: 'toOrder', monthOffset: 0, role: 'plus' },
    { rowKey: 'ratioOrders', monthOffset: 0, role: 'plus' },
  ],
  opCoverage: [
    { rowKey: 'ratioOrders', monthOffset: 0, role: 'plus' },
    { rowKey: 'fixedOrders', monthOffset: 0, role: 'plus' },
    { rowKey: 'availableInventory', monthOffset: 0, role: 'plus' },
    { rowKey: 'demand', monthOffset: 0, role: 'divisor' },
  ],
};
const isFormulaRow = (k: RowKey): boolean => k in FORMULA_DEPS;

// Colors for operand roles (text in popover).
const roleTextColor = (role: OperandRole): string =>
  role === 'plus' ? '#059669' : role === 'minus' ? '#dc2626' : role === 'factor' ? '#2563eb' : '#7c3aed';
// Cell tints per role — [click, hover] intensities.
const roleTint = (role: OperandRole, isClick: boolean): string => {
  const map = {
    plus: ['#bbf7d0', '#dcfce7'],
    minus: ['#fecaca', '#fee2e2'],
    divisor: ['#ddd6fe', '#ede9fe'],
    factor: ['#bfdbfe', '#dbeafe'],
  } as const;
  return map[role][isClick ? 0 : 1];
};

// Generic cell identity so the inspector can span material + beans sections.
const matCellId = (code: string, monthKey: string, rowKey: string) => `m|${code}|${monthKey}|${rowKey}`;
const bmCellId = (monthKey: string) => `bm|${monthKey}`;                    // beans required-per-month
const btCellId = (termCode: string, rowKey: string) => `bt|${termCode}|${rowKey}`; // beans terminal-level
const RESULT_TINT_CLICK = '#fde68a';
const RESULT_TINT_HOVER = '#fef3c7';

// A rendered piece of the substituted expression: operator glue or a colored operand.
type FormulaPart = { op: string } | { value: string; role: OperandRole };

interface FormulaBreakdown {
  formula: string;        // symbolic
  parts: FormulaPart[];   // substituted, colored by role
  result: string;
  note?: string;
}

function buildFormula(rowKey: RowKey, cell: CommodexCell, prevCell: CommodexCell | null): FormulaBreakdown | null {
  const n = (x: number) => fmt(Math.round(x * 100) / 100);
  switch (rowKey) {
    case 'openingInventory':
      if (!prevCell) {
        return {
          formula: 'מלאי on-hand התחלתי',
          parts: [{ value: n(cell.openingInventory), role: 'plus' }],
          result: n(cell.openingInventory),
          note: 'החודש הראשון — מלאי הפתיחה נלקח מהמלאי הפיזי הקיים (on-hand).',
        };
      }
      return {
        formula: 'מלאי הסיום של החודש הקודם',
        parts: [{ value: n(prevCell.closingInventory), role: 'plus' }],
        result: n(cell.openingInventory),
        note: 'מלאי הפתיחה שווה למלאי הסיום של החודש הקודם.',
      };
    case 'closingInventory':
      return {
        formula: 'max(0, מלאי פתיחה + רשיו + מפוקססות − ביקוש)',
        parts: [
          { op: 'max(0, ' },
          { value: n(cell.openingInventory), role: 'plus' },
          { op: ' + ' }, { value: n(cell.ratioOrders), role: 'plus' },
          { op: ' + ' }, { value: n(cell.fixedOrders), role: 'plus' },
          { op: ' − ' }, { value: n(cell.demand), role: 'minus' },
          { op: ')' },
        ],
        result: n(cell.closingInventory),
        note: 'המלאי בסוף החודש — מתגלגל למלאי הפתיחה של החודש הבא.',
      };
    case 'availableInventory':
      return {
        formula: 'max(0, מלאי פתיחה − מלאי חירום)',
        parts: [
          { op: 'max(0, ' },
          { value: n(cell.openingInventory), role: 'plus' },
          { op: ' − ' }, { value: n(cell.emergencyBuffer), role: 'minus' },
          { op: ')' },
        ],
        result: n(cell.availableInventory),
      };
    case 'toOrder':
      return {
        formula: 'max(0, ביקוש − רשיו − מפוקססות − מלאי לניצול)',
        parts: [
          { op: 'max(0, ' },
          { value: n(cell.demand), role: 'plus' },
          { op: ' − ' }, { value: n(cell.ratioOrders), role: 'minus' },
          { op: ' − ' }, { value: n(cell.fixedOrders), role: 'minus' },
          { op: ' − ' }, { value: n(cell.availableInventory), role: 'minus' },
          { op: ')' },
        ],
        result: n(cell.toOrder),
      };
    case 'toPrice':
      return {
        formula: 'כמות להזמין + רשיו',
        parts: [
          { value: n(cell.toOrder), role: 'plus' },
          { op: ' + ' }, { value: n(cell.ratioOrders), role: 'plus' },
        ],
        result: n(cell.toPrice),
      };
    case 'opCoverage':
      return {
        formula: '(רשיו + מפוקססות + מלאי לניצול) / ביקוש',
        parts: [
          { op: '(' },
          { value: n(cell.ratioOrders), role: 'plus' },
          { op: ' + ' }, { value: n(cell.fixedOrders), role: 'plus' },
          { op: ' + ' }, { value: n(cell.availableInventory), role: 'plus' },
          { op: ') / ' }, { value: n(cell.demand), role: 'divisor' },
        ],
        result: fmtPct(cell.opCoverage),
      };
    default:
      return null;
  }
}

// A material-section computed cell (per material, per month).
interface MaterialSelection { kind: 'material'; materialCode: string; monthKey: string; rowKey: RowKey; }
// A beans required-per-month cell.
interface BeansMonthSelection { kind: 'beansMonth'; monthKey: string; }
// A beans terminal-level cell (requiredTerminal / missing / coverage).
interface BeansTerminalSelection { kind: 'beansTerminal'; terminalCode: string; beansRowKey: string; }
type InspectSelection = MaterialSelection | BeansMonthSelection | BeansTerminalSelection;

// Stable identity string for de-duping selections (toggle open/close on re-click).
const selId = (s: InspectSelection): string =>
  s.kind === 'material' ? matCellId(s.materialCode, s.monthKey, s.rowKey)
    : s.kind === 'beansMonth' ? bmCellId(s.monthKey)
      : btCellId(s.terminalCode, s.beansRowKey);

// Beans (פולים) computed rows that support the inspector.
const BEANS_FORMULA_ROWS = new Set(['requiredTerminal', 'missing', 'coverage', 'financialCoverage']);

// Breakdown for a beans cell — spans material sections (per-month) or beans rows.
function buildBeansFormula(sel: BeansMonthSelection | BeansTerminalSelection, data: CommodexData): FormulaBreakdown | null {
  const { terminals, materials, beans } = data;
  const n = (x: number) => fmt(Math.round(x));
  const n2 = (x: number) => (x > 0 ? x.toFixed(2) : '0.00');

  if (sel.kind === 'beansMonth') {
    const parts: FormulaPart[] = [{ op: '(' }];
    materials.forEach((mat, i) => {
      const c = mat.cells[sel.monthKey];
      if (i > 0) parts.push({ op: ' + ' });
      parts.push({ op: 'max(0, ' });
      parts.push({ value: n(c?.demand ?? 0), role: 'plus' });
      parts.push({ op: ' − ' });
      parts.push({ value: n(c?.availableInventory ?? 0), role: 'minus' });
      parts.push({ op: ') × ' });
      parts.push({ value: n2(c?.avgRatio ?? 0), role: 'factor' });
    });
    parts.push({ op: ') ÷ 10' });
    return {
      formula: '(Σ max(0, ביקוש − מלאי לניצול) × רשיו) ÷ 10 טון לחוזה',
      parts,
      result: n(beans.requiredByMonth[sel.monthKey] || 0),
      note: 'לכל חומר: הביקוש שאינו מכוסה במלאי לניצול, מומר לפולים לפי הרשיו, חלקי גודל החוזה. הזמנות רכש אינן מפחיתות את הנדרש — הן נספרות בחוזים המתומחרים.',
    };
  }

  const term = terminals.find(t => t.code === sel.terminalCode);
  const bt = beans.terminals[sel.terminalCode];
  if (!bt) return null;
  if (sel.beansRowKey === 'requiredTerminal') {
    const parts: FormulaPart[] = [];
    (term?.monthKeys ?? []).forEach((mk, i) => {
      if (i > 0) parts.push({ op: ' + ' });
      parts.push({ value: n(beans.requiredByMonth[mk] || 0), role: 'plus' });
    });
    return { formula: 'Σ חוזים נדרשים (חודשי) בטרמינל', parts, result: n(bt.requiredContracts) };
  }
  if (sel.beansRowKey === 'missing') {
    return {
      formula: 'נדרש לטרמינל − חוזים מתומחרים',
      parts: [{ value: n(bt.requiredContracts), role: 'plus' }, { op: ' − ' }, { value: n(bt.onExchangeContracts), role: 'minus' }],
      result: n(bt.missingContracts),
      note: 'חיובי = חוסר לכיסוי, שלילי = עודף.',
    };
  }
  if (sel.beansRowKey === 'coverage') {
    return {
      formula: 'חוזים מתומחרים ÷ נדרש לטרמינל',
      parts: [{ value: n(bt.onExchangeContracts), role: 'plus' }, { op: ' ÷ ' }, { value: n(bt.requiredContracts), role: 'divisor' }],
      result: fmtPct(bt.coveragePct),
    };
  }
  if (sel.beansRowKey === 'financialCoverage') {
    let demandMT = 0;
    for (const mk of term?.monthKeys ?? []) {
      for (const mat of materials) {
        const c = mat.cells[mk];
        if (c) demandMT += c.demand * c.avgRatio;
      }
    }
    return {
      formula: '1 − (נדרש לטרמינל×10 − חוזים מתומחרים×10) ÷ ביקוש שקול-פולים',
      parts: [
        { op: '1 − (' },
        { value: n(bt.requiredContracts * 10), role: 'plus' },
        { op: ' − ' }, { value: n(bt.onExchangeContracts * 10), role: 'minus' },
        { op: ') ÷ ' }, { value: n(demandMT), role: 'divisor' },
      ],
      result: fmtPct(bt.financialCoveragePct),
      note: 'פולים לא מכוסים (טון) = נדרש − מתומחרים; ביקוש שקול-פולים = Σ(ביקוש × רשיו) — לשני החומרים בכל חודשי הטרמינל.',
    };
  }
  return null;
}

// ─── Component ─────────────────────────────────────────────────────────────────

export const CommodexTable: React.FC<Props> = ({ accountId, baseMaterialId }) => {
  const { data: baseHedgeMaterials } = useBaseHedgeMaterials(accountId);
  const { data: formulasPricesData } = useFormulasPrices();

  const baseTemplate = baseHedgeMaterials?.[0];
  const templateId = baseTemplate?.id ? parseInt(baseTemplate.id, 10) : undefined;
  const { data: derivedMaterialsRaw } = useDerivedMaterials(templateId);

  const pricesMap = useMemo(() => {
    if (!formulasPricesData?.pricesMap) return {};
    const map: Record<string, number> = {};
    Object.entries(formulasPricesData.pricesMap).forEach(([code, info]) => { map[code] = info.price; });
    return map;
  }, [formulasPricesData]);

  // View horizon (months) — a personal display preference, saved per account. Sent to the
  // backend so the terminal windows actually cover it (it used to always ask for 12 months).
  const [horizonMonths, setHorizonMonths] = useState<number>(() => {
    const saved = Number(localStorage.getItem(`commodex-horizon-${accountId}`));
    return saved > 0 ? saved : HORIZON_MONTHS;
  });
  const changeHorizon = useCallback((n: number) => {
    setHorizonMonths(n);
    try { localStorage.setItem(`commodex-horizon-${accountId}`, String(n)); } catch { /* ignore */ }
  }, [accountId]);

  const { data: derivationResult } = useQuery(
    ['commodex-derivation', accountId, pricesMap, horizonMonths],
    () => getDerivedHedges(accountId, pricesMap, horizonMonths),
    { enabled: !!accountId && Object.keys(pricesMap).length > 0, staleTime: 30000, refetchOnWindowFocus: false, keepPreviousData: true },
  );
  const allocations = derivationResult?.allocations ?? [];

  const { data: demandPositions = [] } = useQuery(
    ['commodex-demand', accountId],
    () => getDemandPositions({ accountId }),
    { enabled: !!accountId, staleTime: 30000, refetchOnWindowFocus: false },
  );
  const { data: supplyPositions = [] } = useQuery(
    ['commodex-supply', accountId],
    () => getSupplyPositions({ accountId }),
    { enabled: !!accountId, staleTime: 30000, refetchOnWindowFocus: false },
  );
  // Base-formula (beans) exchange hedges — feed the פולים contract-coverage section.
  const { data: hedgePositions = [] } = useQuery(
    ['commodex-hedges', accountId],
    () => getHedgePositions({ accountId }),
    { enabled: !!accountId, staleTime: 30000, refetchOnWindowFocus: false },
  );
  const baseCurrency = useBaseCurrency();

  // Derived materials in calculator shape.
  const derivedMaterials = useMemo<CommodexDerivedMaterial[]>(() => {
    if (!derivedMaterialsRaw) return [];
    return derivedMaterialsRaw.map((dm: any) => ({
      formulaCode: dm.formula_code,
      formulaName: dm.formula_name,
      priceRatio: Number(dm.price_ratio) || 1,
      linkedRatioCode: dm.linked_ratio_code || null,
    }));
  }, [derivedMaterialsRaw]);

  const materialCodes = useMemo(() => new Set(derivedMaterials.map(d => d.formulaCode)), [derivedMaterials]);

  // Map derived material code -> numeric formula id (needed for budget-ratio persistence).
  const formulaIdByCode = useMemo<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    (derivedMaterialsRaw ?? []).forEach((dm: any) => {
      if (dm.derived_formula_id != null) out[dm.formula_code] = Number(dm.derived_formula_id);
    });
    return out;
  }, [derivedMaterialsRaw]);

  // Buffer (emergency stock) config per material — sourced from watch rules exactly
  // like the Buffer policy (useStrategyData): a per-material `buffer_breach` rule when
  // present, otherwise the account-wide rule (formula_id = null), so materials without
  // their own buffer (e.g. Cocoa Mass) inherit the account-wide value instead of 0.
  const { data: watchRules = [] } = useWatchRules(accountId ? String(accountId) : undefined);
  const bufferConfigs = useMemo<Record<string, CommodexBufferConfig>>(() => {
    const out: Record<string, CommodexBufferConfig> = {};
    derivedMaterials.forEach(dm => {
      const numId = formulaIdByCode[dm.formulaCode];
      const raw: any = watchRules.length ? extractPolicyConfig(watchRules, numId).buffer : BUFFER_DEFAULT;
      out[dm.formulaCode] = typeof raw === 'number'
        ? { baseAmount: raw, lookaheadMonths: 0, safetyPercentage: 0 }
        : { baseAmount: Number(raw.baseAmount) || 0, lookaheadMonths: Number(raw.lookaheadMonths) || 0, safetyPercentage: Number(raw.safetyPercentage) || 0 };
    });
    return out;
  }, [derivedMaterials, watchRules, formulaIdByCode]);

  // Demand shift ("what-if") — a personal simulation lens, saved per account. Shifts
  // every demand position's delivery month by N months (forward/backward) before
  // feeding it into the calculator, so the user can preview the effect of demand
  // slipping or accelerating. Nothing is persisted server-side.
  const [demandShiftMonths, setDemandShiftMonths] = useState<number>(() => {
    const saved = Number(localStorage.getItem(`commodex-demand-shift-${accountId}`));
    return Number.isFinite(saved) ? saved : 0;
  });
  const changeDemandShift = useCallback((n: number) => {
    setDemandShiftMonths(n);
    try { localStorage.setItem(`commodex-demand-shift-${accountId}`, String(n)); } catch { /* ignore */ }
  }, [accountId]);

  // Positions in calculator shape.
  const demand = useMemo<CommodexPosition[]>(() =>
    demandPositions
      .filter((p: any) => materialCodes.has(p.formula_code))
      .map((p: any) => ({ formulaCode: p.formula_code, period: p.delivery_period, quantity: Number(p.quantity) || 0 })),
  [demandPositions, materialCodes]);

  const onHand = useMemo<CommodexPosition[]>(() =>
    supplyPositions
      .filter((p: any) => materialCodes.has(p.formula_code) && p.supply_type === 'on_hand_inventory')
      .map((p: any) => ({ formulaCode: p.formula_code, period: p.delivery_or_available_period, quantity: Number(p.quantity) || 0 })),
  [supplyPositions, materialCodes]);

  const ratioPOs = useMemo<CommodexRatioPosition[]>(() =>
    supplyPositions
      .filter((p: any) => materialCodes.has(p.formula_code) && p.supply_type === 'ratio_po')
      .map((p: any) => ({ formulaCode: p.formula_code, period: p.delivery_or_available_period, quantity: Number(p.quantity) || 0, ratio: parseRatio(p.notes) })),
  [supplyPositions, materialCodes]);

  const fixedPOs = useMemo<CommodexRatioPosition[]>(() =>
    supplyPositions
      .filter((p: any) => materialCodes.has(p.formula_code) && p.supply_type === 'purchase_order')
      .map((p: any) => ({ formulaCode: p.formula_code, period: p.delivery_or_available_period, quantity: Number(p.quantity) || 0, ratio: parseRatio(p.notes) })),
  [supplyPositions, materialCodes]);

  // Base-formula hedges (beans) in calculator shape — filter to the selected base material.
  const hedges = useMemo<CommodexHedge[]>(() =>
    (hedgePositions as any[])
      .filter(h => h.formula_code === baseMaterialId && h.maturity_date)
      .map(h => ({
        maturityDate: h.maturity_date,
        quantityMT: Math.abs(Number(h.hedged_quantity_mt) || 0),
        side: h.position_side === 'short' ? -1 : 1,
        price: Number(h.underlying_price || h.formula_price || 0),
        currency: (h.contract_currency || h.hedge_currency || baseCurrency || 'USD').toUpperCase(),
      })),
  [hedgePositions, baseMaterialId, baseCurrency]);

  // Frontend-only avg-ratio overrides (never persisted) — users "play" with the ratio
  // to see its effect on required contracts. Keyed `${formulaCode}|${monthKey}`.
  const [ratioOverrides, setRatioOverrides] = useState<Record<string, number>>({});

  const data = useMemo<CommodexData | null>(() => {
    if (!derivedMaterials.length || !allocations.length) return null;
    return calculateCommodexTable({
      derivedMaterials, allocations, demand, onHand, ratioPOs, fixedPOs, bufferConfigs, pricesMap,
      hedges, baseCurrency, ratioOverrides, horizonMonths, demandShiftMonths,
    });
  }, [derivedMaterials, allocations, demand, onHand, ratioPOs, fixedPOs, bufferConfigs, pricesMap, hedges, baseCurrency, ratioOverrides, horizonMonths, demandShiftMonths]);

  // Budget ratio — per material, per month — persisted to the DB via /commodex.
  const { data: budgetRatioRows = [] } = useCommodexBudgetRatios(accountId);
  const setBudgetMutation = useSetCommodexBudgetRatio(accountId);

  // Server map + local overrides (for instant feedback), keyed by `${formulaCode}|${monthKey}`.
  const [budgetOverrides, setBudgetOverrides] = useState<Record<string, number>>({});
  const serverBudgetMap = useMemo<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    budgetRatioRows.forEach(r => {
      const [y, m] = r.periodMonth.split('-').map(Number);
      out[`${r.formulaCode}|${y}-${m}`] = r.ratio;
    });
    return out;
  }, [budgetRatioRows]);

  // Which cell is currently being edited, and its draft text.
  const [editingBudget, setEditingBudget] = useState<string | null>(null);
  const [draftBudget, setDraftBudget] = useState('');

  const getBudgetRatio = useCallback((fc: string, monthKey: string, fallback: number): { value: number; isSet: boolean } => {
    const key = `${fc}|${monthKey}`;
    if (budgetOverrides[key] !== undefined) return { value: budgetOverrides[key], isSet: true };
    if (serverBudgetMap[key] !== undefined) return { value: serverBudgetMap[key], isSet: true };
    return { value: fallback, isSet: false };
  }, [budgetOverrides, serverBudgetMap]);

  const commitBudget = useCallback((fc: string, monthKey: string, rawValue: string) => {
    const key = `${fc}|${monthKey}`;
    const value = parseFloat(rawValue);
    setEditingBudget(null);
    if (Number.isNaN(value) || value <= 0) return;
    const formulaId = formulaIdByCode[fc];
    if (!formulaId) return;
    setBudgetOverrides(prev => ({ ...prev, [key]: value }));
    setBudgetMutation.mutate({ accountId, formulaId, periodMonth: monthToPeriod(monthKey), ratio: value });
  }, [formulaIdByCode, accountId, setBudgetMutation]);

  // Avg-ratio inline editing (frontend-only overrides — never persisted).
  const [editingRatio, setEditingRatio] = useState<string | null>(null);
  const [draftRatio, setDraftRatio] = useState('');
  const commitRatio = useCallback((fc: string, monthKey: string, rawValue: string) => {
    const key = `${fc}|${monthKey}`;
    setEditingRatio(null);
    const value = parseFloat(rawValue);
    setRatioOverrides(prev => {
      const next = { ...prev };
      if (Number.isNaN(value) || value <= 0) delete next[key]; // clear → back to computed
      else next[key] = value;
      return next;
    });
  }, []);

  // Budget price (beans) — per base material, per terminal maturity month — persisted to DB.
  const { data: budgetPriceRows = [] } = useCommodexBudgetPrices(accountId);
  const setPriceMutation = useSetCommodexBudgetPrice(accountId);

  const [priceOverrides, setPriceOverrides] = useState<Record<string, number>>({});
  const serverPriceMap = useMemo<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    budgetPriceRows.forEach(r => {
      const [y, m] = r.periodMonth.split('-').map(Number);
      out[`${r.baseMaterialCode}|${y}-${m}`] = r.price;
    });
    return out;
  }, [budgetPriceRows]);

  const [editingPrice, setEditingPrice] = useState<string | null>(null);
  const [draftPrice, setDraftPrice] = useState('');

  const getBudgetPrice = useCallback((maturityKey: string, fallback: number): { value: number; isSet: boolean } => {
    const key = `${baseMaterialId}|${maturityKey}`;
    if (priceOverrides[key] !== undefined) return { value: priceOverrides[key], isSet: true };
    if (serverPriceMap[key] !== undefined) return { value: serverPriceMap[key], isSet: true };
    return { value: fallback, isSet: false };
  }, [priceOverrides, serverPriceMap, baseMaterialId]);

  const commitPrice = useCallback((maturityKey: string, rawValue: string) => {
    const key = `${baseMaterialId}|${maturityKey}`;
    const value = parseFloat(rawValue);
    setEditingPrice(null);
    if (Number.isNaN(value) || value <= 0) return;
    setPriceOverrides(prev => ({ ...prev, [key]: value }));
    setPriceMutation.mutate({ accountId, baseMaterialCode: baseMaterialId, periodMonth: monthToPeriod(maturityKey), price: value });
  }, [baseMaterialId, accountId, setPriceMutation]);

  const [showInvDetail, setShowInvDetail] = useState(false);

  // Excel-style formula inspector: hover a computed cell to lightly highlight its
  // inputs; click to lock the highlight and open a sticky breakdown popover.
  const [inspect, setInspect] = useState<InspectSelection | null>(null);
  const [inspectAnchor, setInspectAnchor] = useState<HTMLElement | null>(null);
  const [hoverCell, setHoverCell] = useState<InspectSelection | null>(null);
  const closeInspect = useCallback(() => { setInspect(null); setInspectAnchor(null); }, []);
  const openInspect = useCallback((sel: InspectSelection, anchor: HTMLElement) => {
    const same = (a: InspectSelection | null) => !!a && selId(a) === selId(sel);
    // Inventory rows live in the expandable detail — auto-open so highlights are visible.
    if (sel.kind === 'material' && (sel.rowKey === 'availableInventory' || sel.rowKey === 'openingInventory' || sel.rowKey === 'closingInventory')) setShowInvDetail(true);
    setInspect(prev => (same(prev) ? null : sel));
    setInspectAnchor(() => (same(inspect) ? null : anchor));
  }, [inspect]);

  if (!data || !data.materials.length) {
    return (
      <div className="flex flex-col h-full p-8 items-center justify-center" style={{ color: '#9ca3af' }}>
        <p className="text-sm font-medium">אין נתונים להצגה</p>
        <p className="text-xs mt-1">נדרש תבנית גזירת גידור עם לוח מסחר טרמינלים.</p>
      </div>
    );
  }

  const { months, terminals, materials, beans } = data;
  const visibleRows = ROWS.filter(r => showInvDetail || !r.detail);

  // Beans (פולים) — terminal-level contract-coverage rows.
  const rc = (x: number) => fmt(Math.round(x));
  const missingColor = (x: number) => (Math.round(x) === 0 ? ZERO_COLOR : x > 0 ? '#dc2626' : '#059669');
  // Market price vs. budget: red when the real price exceeded the budget, green otherwise.
  const priceColor = (market: number, budget: number) =>
    market <= 0 ? ZERO_COLOR : budget <= 0 ? '#1f2937' : market > budget ? '#dc2626' : '#059669';
  const beansTerminalRows: Array<{
    key: string; label: string; help: string;
    cell: (_bt?: typeof beans.terminals[string]) => { text: string; color: string };
    total: { text: string; color: string };
  }> = [
    {
      key: 'requiredTerminal', label: 'נדרש לטרמינל', help: 'סך החוזים הנדרשים בכל החודשים שהטרמינל מכסה.',
      cell: bt => ({ text: bt ? rc(bt.requiredContracts) : '—', color: '#1f2937' }),
      total: { text: rc(beans.totals.requiredContracts), color: '#1f2937' },
    },
    {
      key: 'onExchange', label: 'חוזים מתומחרים', help: 'עמדות בבורסה (פולים) שמתבגרות בטרמינל זה + הזמנות רכש (PO׳s) בתקופה.',
      cell: bt => ({ text: bt ? rc(bt.onExchangeContracts) : '—', color: bt && bt.onExchangeContracts ? '#1f2937' : ZERO_COLOR }),
      total: { text: rc(beans.totals.onExchangeContracts), color: beans.totals.onExchangeContracts ? '#1f2937' : ZERO_COLOR },
    },
    {
      key: 'avgPrice', label: `מחיר בורסה ממוצע (${beans.totals.avgCurrency})`, help: 'מחיר ממוצע משוקלל-כמות של חוזי הבורסה בטרמינל.',
      cell: bt => ({ text: bt && bt.avgPrice > 0 ? rc(bt.avgPrice) : '—', color: bt && bt.avgPrice > 0 ? '#1f2937' : ZERO_COLOR }),
      total: { text: beans.totals.avgPrice > 0 ? rc(beans.totals.avgPrice) : '—', color: beans.totals.avgPrice > 0 ? '#1f2937' : ZERO_COLOR },
    },
    {
      key: 'budget', label: `תקציב מחיר (${beans.totals.avgCurrency})`, help: 'מחיר יעד תקציבי להשוואה מול מחיר הבורסה.',
      cell: () => ({ text: rc(beans.budgetPrice), color: '#6b7280' }),
      total: { text: rc(beans.budgetPrice), color: '#6b7280' },
    },
    {
      key: 'missing', label: 'חסר/עודף חוזים', help: 'ההפרש בין הנדרש לקיים בבורסה. חיובי = חוסר לכיסוי, שלילי = עודף.',
      cell: bt => ({ text: bt ? rc(bt.missingContracts) : '—', color: bt ? missingColor(bt.missingContracts) : ZERO_COLOR }),
      total: { text: rc(beans.totals.missingContracts), color: missingColor(beans.totals.missingContracts) },
    },
    {
      key: 'coverage', label: 'אחוז כיסוי חוזים', help: 'שיעור הכיסוי: חוזים מתומחרים ÷ חוזים נדרשים.',
      cell: bt => ({ text: fmtPct(bt ? bt.coveragePct : null), color: opCoverageColor(bt ? bt.coveragePct : null) }),
      total: { text: fmtPct(beans.totals.coveragePct), color: opCoverageColor(beans.totals.coveragePct) },
    },
    {
      key: 'financialCoverage', label: 'כיסוי פיננסי',
      help: 'הכיסוי הפיננסי מתוך הביקוש: 1 − (נדרש×10 − מתומחרים×10) ÷ ביקוש שקול-פולים (Σ ביקוש×רשיו).',
      cell: bt => ({ text: fmtPct(bt ? bt.financialCoveragePct : null), color: opCoverageColor(bt ? bt.financialCoveragePct : null) }),
      total: { text: fmtPct(beans.totals.financialCoveragePct), color: opCoverageColor(beans.totals.financialCoveragePct) },
    },
  ];

  // Resolve the active selection (click wins over hover) into the set of involved cells,
  // using the generic cell-id scheme so highlights can span material + beans sections.
  const activeSel = inspect || hoverCell;
  const activeIsClick = !!inspect;
  let involvedCells: Map<string, OperandRole> | null = null;
  let resultCellId = '';
  if (activeSel) {
    involvedCells = new Map();
    if (activeSel.kind === 'material') {
      const deps = FORMULA_DEPS[activeSel.rowKey];
      const idx = months.findIndex(m => m.key === activeSel.monthKey);
      if (deps && idx >= 0) {
        for (const d of deps) {
          const target = idx + d.monthOffset;
          if (target < 0 || target >= months.length) continue;
          involvedCells.set(matCellId(activeSel.materialCode, months[target].key, d.rowKey), d.role);
        }
        resultCellId = matCellId(activeSel.materialCode, activeSel.monthKey, activeSel.rowKey);
      }
    } else if (activeSel.kind === 'beansMonth') {
      // Required-per-month = Σ max(0, demand − availableInventory) × avgRatio / 10 across materials.
      for (const mat of materials) {
        involvedCells.set(matCellId(mat.formulaCode, activeSel.monthKey, 'demand'), 'plus');
        involvedCells.set(matCellId(mat.formulaCode, activeSel.monthKey, 'availableInventory'), 'minus');
        involvedCells.set(matCellId(mat.formulaCode, activeSel.monthKey, 'avgRatio'), 'factor');
      }
      resultCellId = bmCellId(activeSel.monthKey);
    } else {
      const { terminalCode, beansRowKey } = activeSel;
      if (beansRowKey === 'requiredTerminal') {
        const term = terminals.find(t => t.code === terminalCode);
        for (const mk of term?.monthKeys ?? []) involvedCells.set(bmCellId(mk), 'plus');
      } else if (beansRowKey === 'missing') {
        involvedCells.set(btCellId(terminalCode, 'requiredTerminal'), 'plus');
        involvedCells.set(btCellId(terminalCode, 'onExchange'), 'minus');
      } else if (beansRowKey === 'coverage') {
        involvedCells.set(btCellId(terminalCode, 'onExchange'), 'plus');
        involvedCells.set(btCellId(terminalCode, 'requiredTerminal'), 'divisor');
      } else if (beansRowKey === 'financialCoverage') {
        // (required − priced) over bean-equivalent demand: ביקוש × רשיו across every month the
        // terminal covers, in both material sections.
        const term = terminals.find(t => t.code === terminalCode);
        for (const mk of term?.monthKeys ?? []) {
          for (const mat of materials) {
            involvedCells.set(matCellId(mat.formulaCode, mk, 'demand'), 'divisor');
            involvedCells.set(matCellId(mat.formulaCode, mk, 'avgRatio'), 'factor');
          }
        }
        involvedCells.set(btCellId(terminalCode, 'requiredTerminal'), 'plus');
        involvedCells.set(btCellId(terminalCode, 'onExchange'), 'minus');
      }
      resultCellId = btCellId(terminalCode, beansRowKey);
    }
  }

  // Styles
  const th: React.CSSProperties = { padding: '6px 8px', whiteSpace: 'nowrap', textAlign: 'center' };
  const stickyRight: React.CSSProperties = {
    position: 'sticky', right: 0, zIndex: 10, background: '#fff', minWidth: 170,
    borderLeft: '2px solid #e5e7eb',
  };
  // Total column stays visible on horizontal scroll (leftmost edge in RTL).
  const stickyLeftBase: React.CSSProperties = {
    position: 'sticky', left: 0, zIndex: 10, borderRight: '2px solid #9ca3af',
  };

  return (
    <div
      className="flex flex-col h-full overflow-auto commodex-root"
      dir="rtl"
      style={{ fontFamily: "'Open Sans', 'Heebo', 'Segoe UI', system-ui, sans-serif" }}
    >
      <style>{`
        .commodex-pencil { opacity: 0; transition: opacity .12s ease; color: #2563eb; }
        .commodex-budget-cell:hover .commodex-pencil { opacity: 0.55; }
        .commodex-budget-cell:hover { text-decoration: underline dotted #94a3b8; text-underline-offset: 3px; }
        .commodex-budget-input { -moz-appearance: textfield; appearance: textfield; }
        .commodex-budget-input::-webkit-outer-spin-button,
        .commodex-budget-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        /* Keep the minus/plus sign to the left of numeric values even in this RTL table. */
        .commodex-root .tabular-nums { direction: ltr; unicode-bidi: embed; }
      `}</style>
      <div className="px-4 pt-3 pb-2" style={{ borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <h2 className="text-base font-semibold" style={{ color: '#111827' }}>
            {baseTemplate?.formula_name ?? baseMaterialId} — טבלת קומודקס
          </h2>
          <p className="text-xs mt-0.5" style={{ color: '#9ca3af' }}>
            ביקוש, הזמנות, מלאי וכיסוי לפי חומר וטרמינל · לחצו על תא מחושב לפירוט הנוסחה
          </p>
        </div>
        {/* Demand shift ("what-if") selector — personal simulation lens, saved per account. */}
        <Tooltip title="הזזת ביקוש: מדמה מה קורה אם כל הביקוש מתעכב/מוקדם במספר חודשים. משפיע על התחשיב בלבד, לא נשמר." arrow placement="top">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
            <span>הזזת ביקוש</span>
            <select
              value={demandShiftMonths}
              onChange={e => changeDemandShift(Number(e.target.value))}
              style={{
                fontSize: 12, padding: '3px 8px 3px 26px', border: 'none', borderRadius: 6,
                color: demandShiftMonths !== 0 ? '#b45309' : '#111827', cursor: 'pointer',
                appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
                backgroundColor: demandShiftMonths !== 0 ? '#fef3c7' : '#fff',
                backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='3'><path d='M6 9l6 6 6-6'/></svg>")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'left 10px center',
                backgroundSize: '10px',
              }}
            >
              {DEMAND_SHIFT_OPTIONS.map(n => (
                <option key={n} value={n}>{n === 0 ? 'ללא הזזה' : n > 0 ? `+${n} חודשים` : `${n} חודשים`}</option>
              ))}
            </select>
          </label>
        </Tooltip>
        {/* View horizon selector — personal preference, saved per account. */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
          <span>אופק</span>
          <select
            value={horizonMonths}
            onChange={e => changeHorizon(Number(e.target.value))}
            style={{
              fontSize: 12, padding: '3px 8px 3px 26px', border: 'none', borderRadius: 6,
              color: '#111827', cursor: 'pointer',
              appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
              // Custom chevron so we control the gap to the left edge (native RTL arrow hugs it).
              backgroundColor: '#fff',
              backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='3'><path d='M6 9l6 6 6-6'/></svg>")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'left 10px center',
              backgroundSize: '10px',
            }}
          >
            {HORIZON_OPTIONS.map(n => (
              <option key={n} value={n}>{n} חודשים</option>
            ))}
          </select>
        </label>
      </div>

      <div className="overflow-auto flex-1 px-2 pb-4">
        <table className="text-xs border-separate border-spacing-0 mt-2" style={{ minWidth: '100%' }}>
          <thead>
            {/* Row 1: terminal group headers */}
            <tr>
              <th style={{ ...stickyRight, borderBottom: '2px solid #e5e7eb' }} />
              {terminals.map(t => (
                <th key={`term-${t.code}`} colSpan={t.monthKeys.length} style={{ ...th, borderBottom: '2px solid #d1d5db', borderRight: '2px solid #e5e7eb', background: '#f9fafb' }}>
                  <div className="font-bold" style={{ color: '#374151' }}>{t.label}</div>
                  <div style={{ fontSize: 10, color: '#9ca3af' }}>{t.code}</div>
                </th>
              ))}
              <th rowSpan={2} style={{ ...th, ...stickyLeftBase, borderBottom: '2px solid #d1d5db', background: '#eff6ff', color: '#1f2937', fontWeight: 700 }}>{'סה"כ'}</th>
            </tr>
            {/* Row 2: month labels */}
            <tr>
              <th style={{ ...stickyRight, textAlign: 'right', padding: '6px 12px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>מדד</th>
              {terminals.map(t => t.monthKeys.map((mk, mi) => {
                const m = months.find(x => x.key === mk)!;
                return (
                  <th key={mk} style={{ ...th, borderBottom: '1px solid #e5e7eb', borderRight: mi === 0 ? '2px solid #e5e7eb' : '1px solid #f3f4f6', color: '#6b7280', fontWeight: 400 }}>
                    {m.label}
                  </th>
                );
              }))}
            </tr>
          </thead>

          <tbody>
            {materials.map((mat, mIdx) => {
              const budgetFallback = Math.round(mat.fallbackRatio * 100) / 100;
              return (
                <React.Fragment key={mat.formulaCode}>
                  {/* Section header */}
                  <tr>
                    <td colSpan={1 + months.length + 1} style={{
                      background: mIdx === 0 ? '#eef2ff' : '#f0fdf4',
                      color: '#111827', fontWeight: 700, padding: '6px 12px',
                      borderTop: '2px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', textAlign: 'right',
                    }}>
                      {mat.formulaName}
                    </td>
                  </tr>

                  {visibleRows.map((row, ri) => {
                    const rowBg = ri % 2 === 0 ? '#ffffff' : '#fafafa';
                    const isInvParent = row.key === 'availableInventory';
                    return (
                      <tr key={`${mat.formulaCode}-${row.key}`} style={{ background: rowBg }}>
                        <td style={{ ...stickyRight, background: rowBg, textAlign: 'right', padding: '5px 12px', borderBottom: '1px solid #f3f4f6', color: row.detail ? '#9ca3af' : '#374151', fontWeight: row.detail ? 400 : 500 }}>
                          <div className="flex items-center gap-1" style={{ justifyContent: 'flex-start' }}>
                            {isInvParent && (
                              <button onClick={() => setShowInvDetail(v => !v)} style={{ color: '#2563eb', background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex' }}>
                                {showInvDetail ? <UnfoldLess style={{ fontSize: 14 }} /> : <UnfoldMore style={{ fontSize: 14 }} />}
                              </button>
                            )}
                            <Tooltip
                              title={<span style={{ display: 'block', direction: 'rtl', textAlign: 'right', fontSize: 12, lineHeight: 1.5 }}>{row.help}</span>}
                              placement="left" arrow
                            >
                              <span style={{ cursor: 'help' }}>{row.label}</span>
                            </Tooltip>
                          </div>
                        </td>

                        {terminals.map(t => t.monthKeys.map((mk, mi) => {
                          const cell = mat.cells[mk];
                          const borderRight = mi === 0 ? '2px solid #e5e7eb' : '1px solid #f3f4f6';
                          if (!cell) return <td key={mk} style={{ borderBottom: '1px solid #f3f4f6', borderRight }} />;

                          // Budget ratio — individually editable per month cell.
                          if (row.key === 'budgetRatio') {
                            const bKey = `${mat.formulaCode}|${mk}`;
                            const { value: bVal, isSet } = getBudgetRatio(mat.formulaCode, mk, budgetFallback);
                            const isEditing = editingBudget === bKey;
                            return (
                              <td key={mk} style={{ ...th, width: 58, borderBottom: '1px solid #f3f4f6', borderRight, background: rowBg }}>
                                {/* Fixed-width wrapper so edit/display never change the column width. */}
                                <div style={{ position: 'relative', width: 48, height: 17, margin: '0 auto' }}>
                                  {isEditing ? (
                                    <input
                                      autoFocus type="number" step="0.01" value={draftBudget}
                                      className="commodex-budget-input"
                                      onChange={e => setDraftBudget(e.target.value)}
                                      onBlur={e => commitBudget(mat.formulaCode, mk, e.target.value)}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                        if (e.key === 'Escape') setEditingBudget(null);
                                      }}
                                      style={{
                                        width: '100%', height: 17, boxSizing: 'border-box', margin: 0,
                                        textAlign: 'center', border: '1px solid #2563eb', borderRadius: 4, padding: '0 2px',
                                        fontSize: 11, lineHeight: '15px', fontFamily: 'inherit',
                                      }}
                                    />
                                  ) : (
                                    <span
                                      className="commodex-budget-cell"
                                      onClick={() => { setEditingBudget(bKey); setDraftBudget(String(bVal)); }}
                                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: 17, lineHeight: '17px', cursor: 'pointer', color: isSet ? '#1f2937' : '#94a3b8', fontStyle: isSet ? 'normal' : 'italic' }}
                                    >
                                      <span className="tabular-nums">{fmtRatio(bVal)}</span>
                                      {/* Pencil floats over the wrapper — no layout width. */}
                                      <Edit className="commodex-pencil" style={{ fontSize: 11, position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)' }} />
                                    </span>
                                  )}
                                </div>
                              </td>
                            );
                          }

                          // Avg ratio — inline-editable (frontend-only "what-if"; not stored).
                          if (row.key === 'avgRatio') {
                            const rKey = `${mat.formulaCode}|${mk}`;
                            const isOverridden = ratioOverrides[rKey] !== undefined;
                            const isEditing = editingRatio === rKey;
                            const rColor = isOverridden ? '#7c3aed' : (!cell.hasOrders ? FALLBACK_RATIO_COLOR : '#1f2937');
                            const rCellId = matCellId(mat.formulaCode, mk, 'avgRatio');
                            const rInvolved = involvedCells?.get(rCellId);
                            const rBg = rInvolved ? roleTint(rInvolved, activeIsClick) : rowBg;
                            return (
                              <td key={mk} style={{ ...th, width: 58, borderBottom: '1px solid #f3f4f6', borderRight, background: rBg }}>
                                <div style={{ position: 'relative', width: 48, height: 17, margin: '0 auto' }}>
                                  {isEditing ? (
                                    <input
                                      autoFocus type="number" step="0.01" value={draftRatio}
                                      className="commodex-budget-input"
                                      onChange={e => setDraftRatio(e.target.value)}
                                      onBlur={e => commitRatio(mat.formulaCode, mk, e.target.value)}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                        if (e.key === 'Escape') setEditingRatio(null);
                                      }}
                                      style={{
                                        width: '100%', height: 17, boxSizing: 'border-box', margin: 0,
                                        textAlign: 'center', border: '1px solid #7c3aed', borderRadius: 4, padding: '0 2px',
                                        fontSize: 11, lineHeight: '15px', fontFamily: 'inherit',
                                      }}
                                    />
                                  ) : (
                                    <span
                                      className="commodex-budget-cell"
                                      onClick={() => { setEditingRatio(rKey); setDraftRatio(String(Math.round(cell.avgRatio * 100) / 100)); }}
                                      title={isOverridden ? 'ערך ידני (זמני) — נקה כדי לחזור לחישוב' : undefined}
                                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: 17, lineHeight: '17px', cursor: 'pointer', color: rColor, fontStyle: (!cell.hasOrders && !isOverridden) ? 'italic' : 'normal' }}
                                    >
                                      <span className="tabular-nums">{fmtRatio(cell.avgRatio)}</span>
                                      <Edit className="commodex-pencil" style={{ fontSize: 11, position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)' }} />
                                    </span>
                                  )}
                                </div>
                              </td>
                            );
                          }

                          const v = cellValue(row.key, cell);
                          let color = '#1f2937';
                          const fontStyle: 'normal' | 'italic' = 'normal';
                          if (row.key === 'opCoverage') color = opCoverageColor(cell.opCoverage);
                          if (row.key === 'toOrder' && cell.toOrder > 0) color = '#dc2626';
                          // Grey out plain zeros to keep the grid calm.
                          if (color === '#1f2937' && !v) color = ZERO_COLOR;

                          // Formula inspector: highlight this cell if it's the active result
                          // or one of its inputs (role-tinted). Hover = light, click = strong.
                          const clickable = isFormulaRow(row.key);
                          const cellId = matCellId(mat.formulaCode, mk, row.key);
                          const isResult = cellId === resultCellId;
                          const involvedRole = involvedCells?.get(cellId);
                          let cellBg = rowBg;
                          if (isResult) cellBg = activeIsClick ? RESULT_TINT_CLICK : RESULT_TINT_HOVER;
                          else if (involvedRole) cellBg = roleTint(involvedRole, activeIsClick);
                          const matSel: MaterialSelection = { kind: 'material', materialCode: mat.formulaCode, monthKey: mk, rowKey: row.key };

                          return (
                            <td
                              key={mk}
                              onClick={clickable ? (e) => openInspect(matSel, e.currentTarget as HTMLElement) : undefined}
                              onMouseEnter={clickable ? () => setHoverCell(matSel) : undefined}
                              onMouseLeave={clickable ? () => setHoverCell(null) : undefined}
                              style={{
                                ...th, borderBottom: '1px solid #f3f4f6', borderRight, background: cellBg,
                                cursor: clickable ? 'pointer' : 'default',
                                outline: isResult && activeIsClick ? '2px solid #f59e0b' : 'none', outlineOffset: -2,
                                transition: 'background 0.1s ease',
                              }}
                            >
                              <span style={{ color, fontStyle }} className="tabular-nums">{formatValue(row, v)}</span>
                            </td>
                          );
                        }))}

                        {/* Total column */}
                        {(() => {
                          let tv: number | null = null;
                          let content = '';
                          switch (row.key) {
                            case 'demand':       tv = mat.totals.demand; break;
                            case 'ratioOrders':  tv = mat.totals.ratioOrders; break;
                            case 'fixedOrders':  tv = mat.totals.fixedOrders; break;
                            case 'toOrder':      tv = mat.totals.toOrder; break;
                            case 'toPrice':      tv = mat.totals.toPrice; break;
                            case 'avgRatio':     content = fmtRatio(mat.totals.avgRatio); break;
                            case 'budgetRatio': {
                              // Show the mean budget ratio across the year.
                              const vals = months.map(mm => getBudgetRatio(mat.formulaCode, mm.key, budgetFallback).value);
                              content = fmtRatio(vals.reduce((a, b) => a + b, 0) / (vals.length || 1));
                              break;
                            }
                            case 'opCoverage':   content = fmtPct(mat.totals.opCoverage); break;
                            // Inventory rows are rolling stocks, not flows — their "total" is
                            // the position at the edge of the window: opening = first month,
                            // closing/available/buffer = last month (the end-of-horizon state).
                            case 'openingInventory': tv = mat.cells[months[0]?.key]?.openingInventory ?? null; break;
                            case 'closingInventory': tv = mat.cells[months[months.length - 1]?.key]?.closingInventory ?? null; break;
                            case 'availableInventory': tv = mat.cells[months[months.length - 1]?.key]?.availableInventory ?? null; break;
                            case 'emergencyBuffer':  tv = mat.cells[months[months.length - 1]?.key]?.emergencyBuffer ?? null; break;
                            default: break;
                          }
                          if (!content) content = tv !== null ? fmt(tv) : '';
                          const isZeroTotal = content === '' || content === '0';
                          const totalColor = row.key === 'opCoverage'
                            ? opCoverageColor(mat.totals.opCoverage)
                            : (isZeroTotal ? ZERO_COLOR : '#1f2937');
                          return (
                            <td style={{ ...th, ...stickyLeftBase, borderBottom: '1px solid #f3f4f6', background: '#eef4ff', fontWeight: 600 }}>
                              <span className="tabular-nums" style={{ color: totalColor }}>{content}</span>
                            </td>
                          );
                        })()}
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}

            {/* ─── Beans (פולים) — exchange-contract coverage ─── */}
            <tr>
              <td colSpan={1 + months.length + 1} style={{
                background: '#fef3c7', color: '#111827', fontWeight: 700, padding: '6px 12px',
                borderTop: '2px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', textAlign: 'right',
              }}>
                {'פולים — כיסוי חוזי בורסה (חוזים)'}
              </td>
            </tr>

            {/* Required contracts — per month */}
            {(() => {
              const rowBg = '#ffffff';
              return (
                <tr style={{ background: rowBg }}>
                  <td style={{ ...stickyRight, background: rowBg, textAlign: 'right', padding: '5px 12px', borderBottom: '1px solid #f3f4f6', color: '#374151', fontWeight: 500 }}>
                    <Tooltip title={<span style={{ display: 'block', direction: 'rtl', textAlign: 'right', fontSize: 12, lineHeight: 1.5 }}>{'החוזים הנדרשים לכיסוי הכמות לתמחר, פר חודש: Σ(כמות לתמחר × רשיו) ÷ 10 טון לחוזה.'}</span>} placement="left" arrow>
                      <span style={{ cursor: 'help' }}>{'חוזים נדרשים (חודשי)'}</span>
                    </Tooltip>
                  </td>
                  {terminals.map(t => t.monthKeys.map((mk, mi) => {
                    const v = beans.requiredByMonth[mk] || 0;
                    const borderRight = mi === 0 ? '2px solid #e5e7eb' : '1px solid #f3f4f6';
                    const cellId = bmCellId(mk);
                    const isResult = cellId === resultCellId;
                    const involvedRole = involvedCells?.get(cellId);
                    let bg = rowBg;
                    if (isResult) bg = activeIsClick ? RESULT_TINT_CLICK : RESULT_TINT_HOVER;
                    else if (involvedRole) bg = roleTint(involvedRole, activeIsClick);
                    const sel: BeansMonthSelection = { kind: 'beansMonth', monthKey: mk };
                    return (
                      <td
                        key={mk}
                        onClick={(e) => openInspect(sel, e.currentTarget as HTMLElement)}
                        onMouseEnter={() => setHoverCell(sel)}
                        onMouseLeave={() => setHoverCell(null)}
                        style={{
                          ...th, borderBottom: '1px solid #f3f4f6', borderRight, background: bg, cursor: 'pointer',
                          outline: isResult && activeIsClick ? '2px solid #f59e0b' : 'none', outlineOffset: -2,
                          transition: 'background 0.1s ease',
                        }}
                      >
                        <span className="tabular-nums" style={{ color: v ? '#1f2937' : ZERO_COLOR }}>{rc(v)}</span>
                      </td>
                    );
                  }))}
                  <td style={{ ...th, ...stickyLeftBase, borderBottom: '1px solid #f3f4f6', background: '#eef4ff', fontWeight: 600 }}>
                    <span className="tabular-nums">{rc(beans.totals.requiredContracts)}</span>
                  </td>
                </tr>
              );
            })()}

            {/* Terminal-level rows */}
            {beansTerminalRows.map((br, i) => {
              const rowBg = i % 2 === 0 ? '#fafafa' : '#ffffff';
              const isBudgetRow = br.key === 'budget';
              const isAvgPriceRow = br.key === 'avgPrice';
              const isFinCovRow = br.key === 'financialCoverage';
              // Per-terminal budget prices (keyed by terminal maturity month) — used for
              // editing (budget row) and for the market-price comparison colouring.
              const budgetByTerm: Record<string, number> = {};
              terminals.forEach(t => {
                const mKey = t.monthKeys[t.monthKeys.length - 1];
                budgetByTerm[t.code] = getBudgetPrice(mKey, beans.budgetPrice).value;
              });
              const setBudgets = terminals.map(t => budgetByTerm[t.code]).filter(v => v > 0);
              const meanBudget = setBudgets.length ? setBudgets.reduce((a, b) => a + b, 0) / setBudgets.length : beans.budgetPrice;
              return (
                <tr key={`beans-${br.key}`} style={{ background: rowBg }}>
                  <td style={{ ...stickyRight, background: rowBg, textAlign: 'right', padding: '5px 12px', borderBottom: '1px solid #f3f4f6', color: '#374151', fontWeight: isFinCovRow ? 700 : 500 }}>
                    <Tooltip title={<span style={{ display: 'block', direction: 'rtl', textAlign: 'right', fontSize: 12, lineHeight: 1.5 }}>{br.help}</span>} placement="left" arrow>
                      <span style={{ cursor: 'help' }}>{br.label}</span>
                    </Tooltip>
                  </td>
                  {terminals.map(t => {
                    const borderRight = '2px solid #e5e7eb';

                    // ── Budget price — editable per terminal (maturity month). ──
                    if (isBudgetRow) {
                      const mKey = t.monthKeys[t.monthKeys.length - 1];
                      const pKey = `${baseMaterialId}|${mKey}`;
                      const { value: pVal, isSet } = getBudgetPrice(mKey, beans.budgetPrice);
                      const isEditing = editingPrice === pKey;
                      return (
                        <td key={t.code} colSpan={t.monthKeys.length} style={{ ...th, borderBottom: '1px solid #f3f4f6', borderRight, background: rowBg }}>
                          <div style={{ position: 'relative', width: 68, height: 17, margin: '0 auto' }}>
                            {isEditing ? (
                              <input
                                autoFocus type="number" step="1" value={draftPrice}
                                className="commodex-budget-input"
                                onChange={e => setDraftPrice(e.target.value)}
                                onBlur={e => commitPrice(mKey, e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                  if (e.key === 'Escape') setEditingPrice(null);
                                }}
                                style={{
                                  width: '100%', height: 17, boxSizing: 'border-box', margin: 0,
                                  textAlign: 'center', border: '1px solid #2563eb', borderRadius: 4, padding: '0 2px',
                                  fontSize: 11, lineHeight: '15px', fontFamily: 'inherit',
                                }}
                              />
                            ) : (
                              <span
                                className="commodex-budget-cell"
                                onClick={() => { setEditingPrice(pKey); setDraftPrice(String(pVal)); }}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: 17, lineHeight: '17px', cursor: 'pointer', color: isSet ? '#1f2937' : '#94a3b8', fontStyle: isSet ? 'normal' : 'italic' }}
                              >
                                <span className="tabular-nums">{rc(pVal)}</span>
                                <Edit className="commodex-pencil" style={{ fontSize: 11, position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)' }} />
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    }

                    const bt = beans.terminals[t.code];
                    const cellOut = br.cell(bt);
                    const text = cellOut.text;
                    // Colour the market price by budget comparison: red if it exceeded budget, green otherwise.
                    const color = isAvgPriceRow
                      ? priceColor(bt && bt.avgPrice > 0 ? bt.avgPrice : 0, budgetByTerm[t.code])
                      : cellOut.color;
                    const clickable = BEANS_FORMULA_ROWS.has(br.key);
                    const cellId = btCellId(t.code, br.key);
                    const isResult = cellId === resultCellId;
                    const involvedRole = involvedCells?.get(cellId);
                    let bg = rowBg;
                    if (isResult) bg = activeIsClick ? RESULT_TINT_CLICK : RESULT_TINT_HOVER;
                    else if (involvedRole) bg = roleTint(involvedRole, activeIsClick);
                    const sel: BeansTerminalSelection = { kind: 'beansTerminal', terminalCode: t.code, beansRowKey: br.key };
                    return (
                      <td
                        key={t.code} colSpan={t.monthKeys.length}
                        onClick={clickable ? (e) => openInspect(sel, e.currentTarget as HTMLElement) : undefined}
                        onMouseEnter={clickable ? () => setHoverCell(sel) : undefined}
                        onMouseLeave={clickable ? () => setHoverCell(null) : undefined}
                        style={{
                          ...th, borderBottom: '1px solid #f3f4f6', borderRight, background: bg,
                          cursor: clickable ? 'pointer' : 'default',
                          outline: isResult && activeIsClick ? '2px solid #f59e0b' : 'none', outlineOffset: -2,
                          transition: 'background 0.1s ease',
                        }}
                      >
                        <span className="tabular-nums" style={{ color, fontWeight: isFinCovRow ? 700 : undefined }}>{text}</span>
                      </td>
                    );
                  })}
                  {/* Total column */}
                  <td style={{ ...th, ...stickyLeftBase, borderBottom: '1px solid #f3f4f6', background: '#eef4ff', fontWeight: isFinCovRow ? 800 : 600 }}>
                    <span className="tabular-nums" style={{
                      color: isBudgetRow ? '#6b7280'
                        : isAvgPriceRow ? priceColor(beans.totals.avgPrice > 0 ? beans.totals.avgPrice : 0, meanBudget)
                        : br.total.color,
                    }}>
                      {isBudgetRow ? rc(meanBudget) : br.total.text}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Formula inspector popover — stays open until dismissed. */}
      {inspect && inspectAnchor && (() => {
        let fb: FormulaBreakdown | null = null;
        let rowLabel = '';
        let rowHelp = '';
        let contextLabel = '';
        if (inspect.kind === 'material') {
          const mat = materials.find(m => m.formulaCode === inspect.materialCode);
          const cell = mat?.cells[inspect.monthKey];
          if (!mat || !cell) return null;
          const monthIdx = months.findIndex(m => m.key === inspect.monthKey);
          const prevCell = monthIdx > 0 ? mat.cells[months[monthIdx - 1].key] ?? null : null;
          fb = buildFormula(inspect.rowKey, cell, prevCell);
          rowLabel = ROWS.find(r => r.key === inspect.rowKey)?.label ?? '';
          rowHelp = ROWS.find(r => r.key === inspect.rowKey)?.help ?? '';
          contextLabel = `${mat.formulaName} · ${months.find(m => m.key === inspect.monthKey)?.label ?? ''}`;
        } else if (inspect.kind === 'beansMonth') {
          fb = buildBeansFormula(inspect, data);
          rowLabel = 'חוזים נדרשים (חודשי)';
          contextLabel = `פולים · ${months.find(m => m.key === inspect.monthKey)?.label ?? ''}`;
        } else {
          fb = buildBeansFormula(inspect, data);
          rowLabel = beansTerminalRows.find(r => r.key === inspect.beansRowKey)?.label ?? '';
          rowHelp = beansTerminalRows.find(r => r.key === inspect.beansRowKey)?.help ?? '';
          contextLabel = `פולים · ${inspect.terminalCode}`;
        }
        return (
          <Popover
            open anchorEl={inspectAnchor} onClose={closeInspect}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            hideBackdrop disableScrollLock
            slotProps={{ root: { style: { pointerEvents: 'none' } } }}
            PaperProps={{ sx: { pointerEvents: 'auto', borderRadius: 2, boxShadow: '0 8px 28px rgba(0,0,0,0.16)' } }}
          >
            <div dir="rtl" style={{ minWidth: 250, maxWidth: 340, textAlign: 'right', fontFamily: 'inherit' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#fde68a', borderBottom: '1px solid #fcd34d' }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>{rowLabel}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, color: '#92400e' }}>{contextLabel}</span>
                  <Close onClick={closeInspect} style={{ fontSize: 15, cursor: 'pointer', color: '#92400e' }} />
                </div>
              </div>
              <div style={{ padding: '10px 12px', fontSize: 12, lineHeight: 1.7, color: '#374151' }}>
                {fb ? (
                  <>
                    <div style={{ color: '#6b7280', direction: 'rtl' }}>{fb.formula}</div>
                    <div style={{ margin: '5px 0', direction: 'ltr', textAlign: 'right', fontFamily: 'monospace', fontSize: 12.5 }}>
                      {fb.parts.map((p, i) =>
                        'op' in p
                          ? <span key={i} style={{ color: '#9ca3af' }}>{p.op}</span>
                          : <span key={i} style={{ color: roleTextColor(p.role), fontWeight: 600 }}>{p.value}</span>,
                      )}
                    </div>
                    <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px dashed #e5e7eb', fontWeight: 700, color: '#111827' }}>
                      = {fb.result}
                    </div>
                    {fb.note && <div style={{ marginTop: 8, fontSize: 11, color: '#6b7280' }}>{fb.note}</div>}
                    <div style={{ marginTop: fb.note ? 4 : 8, fontSize: 11, color: '#9ca3af' }}>{rowHelp}</div>
                  </>
                ) : (
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{rowHelp}</div>
                )}
              </div>
            </div>
          </Popover>
        );
      })()}
    </div>
  );
};
